/**
 * Dialog zum Anlegen einer strukturierten Zahlungsvorlage. Der Editor selbst (Kommentar, Raten,
 * Summenpruefung) ist derselbe wie auf der Seite "Zahlungsvorlagen" - siehe
 * `PaymentTemplates/VorlagenEditor.tsx`; dieser Dialog ist nur die Huelle mit Abbrechen/Speichern.
 */
import { useState, type JSX, type MouseEvent } from "react";
import { Ikone } from "../Ikone.tsx";
import { vorlageAnlegen, type PaymentTemplateRow } from "../../api/zahlungsvorlagen.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import {
  VorlagenEditor,
  leererEntwurf,
  pruefeEntwurf,
  type VorlagenEntwurf,
} from "../PaymentTemplates/VorlagenEditor.tsx";

export interface NeueVorlageDialogProps {
  onAngelegt: (vorlage: PaymentTemplateRow) => void | Promise<void>;
  onSchliessen: () => void;
  onLaufstatusAendern?: (laeuft: boolean) => void;
  /** Satz unter der Ueberschrift; die Seite "Zahlungsvorlagen" braucht den Lieferantenbezug nicht. */
  hinweis?: string;
}

export function NeueVorlageDialog({
  onAngelegt,
  onSchliessen,
  onLaufstatusAendern,
  hinweis = "Danach kann sie direkt für diesen Lieferanten freigegeben werden.",
}: NeueVorlageDialogProps): JSX.Element {
  const [entwurf, setEntwurf] = useState<VorlagenEntwurf>(leererEntwurf);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern(): Promise<void> {
    if (laeuft) return;
    const ergebnis = pruefeEntwurf(entwurf);
    if (ergebnis.fehler !== null) {
      setFehler(ergebnis.fehler);
      return;
    }
    setFehler(null);
    setLaeuft(true);
    onLaufstatusAendern?.(true);
    try {
      await onAngelegt(await vorlageAnlegen(ergebnis.comment, ergebnis.rows));
    } catch (grund) {
      setFehler(fehlertext(grund, "Die Vorlage konnte nicht angelegt werden."));
    } finally {
      setLaeuft(false);
      onLaufstatusAendern?.(false);
    }
  }

  function schliessen(): void {
    if (!laeuft) onSchliessen();
  }

  function hintergrundKlick(event: MouseEvent<HTMLDivElement>): void {
    if (event.target === event.currentTarget) schliessen();
  }

  return (
    <div className="board-editor-overlay open" role="presentation" onClick={hintergrundKlick}>
      <section
        className="board-editor-dialog neue-vorlage-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="neueVorlageTitel"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="neueVorlageTitel">Neue Zahlungsvorlage</h2>
            <p>{hinweis}</p>
          </div>
          <button className="icon-btn" type="button" onClick={schliessen} disabled={laeuft} title="Abbrechen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body">
          <VorlagenEditor entwurf={entwurf} onChange={setEntwurf} gesperrt={laeuft} kennung="neueVorlage" />
          {fehler ? (
            <p role="alert" className="supplier-extras-fehler">
              {fehler}
            </p>
          ) : null}
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" disabled={laeuft} onClick={schliessen}>
            <Ikone name="x" /> Abbrechen
          </button>
          <button className="btn-primary" type="button" disabled={laeuft} onClick={() => void speichern()}>
            <Ikone name="circle-plus" /> Vorlage speichern
          </button>
        </div>
      </section>
    </div>
  );
}
